import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  GraduationCap, Code, Cog, Rocket, Users, Brain, 
  BookOpen, ScanFace, Share2, Bot, ExternalLink,
  Github, Linkedin, Mail, MapPin
} from 'lucide-react';

const iconMap = {
  GraduationCap, Code, Cog, Rocket, Users, Brain,
  BookOpen, ScanFace, Share2, Bot
};

export const InfoCard = ({ isOpen, onClose, item, theme }) => {
  if (!item) return null;

  const renderEducationCard = () => (
    <div className="space-y-4">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg">
          {item.icon && iconMap[item.icon] ? 
            React.createElement(iconMap[item.icon], { className: "w-7 h-7 text-white" }) :
            <GraduationCap className="w-7 h-7 text-white" />
          }
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-bold text-slate-800">{item.title}</h3>
          <p className="text-emerald-600 font-semibold">{item.institution}</p>
          <Badge variant="outline" className="mt-2">{item.period}</Badge>
        </div>
      </div>
      
      {item.description && (
        <p className="text-slate-600 leading-relaxed">{item.description}</p>
      )}
      
      {item.achievements && item.achievements.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-slate-700">Key Learnings:</h4>
          <div className="flex flex-wrap gap-2">
            {item.achievements.map((achievement, i) => (
              <Badge key={i} variant="secondary" className="bg-emerald-100 text-emerald-700 text-xs">
                {achievement}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderSkillsCard = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-amber-600 capitalize">{item.category?.replace(/([A-Z])/g, ' $1')}</h3>
      {Array.isArray(item.data) && item.data[0]?.level ? (
        <div className="space-y-3">
          {item.data.map((skill, i) => (
            <div key={i}>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-slate-700">{skill.name}</span>
                <span className="text-sm text-amber-600 font-bold">{skill.level}%</span>
              </div>
              <Progress value={skill.level} className="h-2" />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {item.data?.map((skill, i) => (
            <Badge key={i} className="bg-amber-100 text-amber-800 hover:bg-amber-200">
              {skill}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );

  const renderExperienceCard = () => (
    <div className="space-y-4">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-slate-500 to-slate-700 flex items-center justify-center shadow-lg">
          {item.icon && iconMap[item.icon] ? 
            React.createElement(iconMap[item.icon], { className: "w-7 h-7 text-white" }) :
            <Rocket className="w-7 h-7 text-white" />
          }
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-bold text-slate-800">{item.title}</h3>
            {item.status === 'ACTIVE' && (
              <Badge className="bg-green-500 text-white animate-pulse">Active</Badge>
            )}
          </div>
          <p className="text-purple-600 font-semibold">{item.company}</p>
          <Badge variant="outline" className="mt-1">{item.period}</Badge>
        </div>
      </div>
      <p className="text-slate-600 leading-relaxed">{item.description}</p>
    </div>
  );

  const renderProjectCard = () => (
    <div className="space-y-4">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center shadow-lg">
          {item.icon && iconMap[item.icon] ? 
            React.createElement(iconMap[item.icon], { className: "w-7 h-7 text-white" }) :
            <Code className="w-7 h-7 text-white" />
          }
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-bold text-slate-800">{item.title}</h3>
          <div className="flex flex-wrap gap-1 mt-2">
            {item.tags?.map((tag, i) => (
              <Badge key={i} variant="secondary" className="bg-purple-100 text-purple-700">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      </div>
      <p className="text-slate-600 leading-relaxed">{item.description}</p>
      <div className="flex flex-wrap gap-2">
        {item.tech?.map((t, i) => (
          <Badge key={i} variant="outline" className="text-xs">
            {t}
          </Badge>
        ))}
      </div>
    </div>
  );

  const renderContactCard = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 md:w-20 md:h-20 mx-auto rounded-full bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center shadow-xl mb-4">
          <span className="text-2xl md:text-3xl font-bold text-white">A</span>
        </div>
        <h3 className="text-2xl font-bold text-slate-800">{item.name}</h3>
        <p className="text-rose-500 font-semibold">{item.title}</p>
        <div className="flex items-center justify-center gap-1 text-slate-500 mt-1">
          <MapPin className="w-4 h-4" />
          <span className="text-sm">{item.location}</span>
        </div>
      </div>
      
      <p className="text-center text-slate-600 italic">"{item.tagline}"</p>
      
      <div className="flex justify-center gap-4">
        <a 
          href={item.github} 
          target="_blank" 
          rel="noopener noreferrer"
          className="w-12 h-12 rounded-xl bg-slate-900 hover:bg-slate-700 flex items-center justify-center transition-colors"
        >
          <Github className="w-6 h-6 text-white" />
        </a>
        <a 
          href={item.linkedin} 
          target="_blank" 
          rel="noopener noreferrer"
          className="w-12 h-12 rounded-xl bg-blue-600 hover:bg-blue-500 flex items-center justify-center transition-colors"
        >
          <Linkedin className="w-6 h-6 text-white" />
        </a>
        <a 
          href={`mailto:${item.email}`}
          className="w-12 h-12 rounded-xl bg-rose-500 hover:bg-rose-400 flex items-center justify-center transition-colors"
        >
          <Mail className="w-6 h-6 text-white" />
        </a>
      </div>
    </div>
  );

  const renderNPCCard = () => (
    <div className="text-center py-4">
      <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center shadow-lg mb-4">
        <span className="text-2xl md:text-3xl">💬</span>
      </div>
      <p className="text-base md:text-lg text-slate-700 font-medium italic mb-4">"{item.fact}"</p>
    </div>
  );

  const getCardContent = () => {
    if (item.type === 'npc') return renderNPCCard();
    switch (theme) {
      case 'education': return renderEducationCard();
      case 'skills': return renderSkillsCard();
      case 'experience': return renderExperienceCard();
      case 'projects': return renderProjectCard();
      case 'contact': return renderContactCard();
      default: return null;
    }
  };

  const getTitle = () => {
    if (item.type === 'npc') return 'Fun Fact!';
    switch (theme) {
      case 'education': return '📚 Education Unlocked!';
      case 'skills': return '⚡ Skills Discovered!';
      case 'experience': return '💼 Experience Found!';
      case 'projects': return '🚀 Project Revealed!';
      case 'contact': return '🤝 Let\'s Connect!';
      default: return 'Info Unlocked!';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md border-0 shadow-2xl info-card-mobile dialogue-mobile max-h-[80vh] overflow-y-auto" data-testid="info-card-dialog">
        <div className="dialogue-mobile p-4 md:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg md:text-2xl font-bold bg-gradient-to-r from-sky-500 to-purple-600 bg-clip-text text-transparent">
              {getTitle()}
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">
              {item.type === 'npc' ? 'An NPC shared some wisdom!' : 'You discovered something about Akhil!'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            {getCardContent()}
          </div>
          
          <button
            onClick={onClose}
            className="mt-4 w-full py-3 bg-gradient-to-r from-sky-500 to-purple-600 text-white font-bold rounded-xl hover:opacity-90 transition-opacity text-sm md:text-base"
            data-testid="continue-btn"
          >
            Continue Playing →
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InfoCard;
